import { theme } from "@bokio/designsystem/theme/theme";

import type { FC } from "react";

import * as styles from "./progressBar.scss";

type ProgressBarColor = "primary" | "warn" | "lilac" | "dark";

interface ProgressBarProps {
	percent?: number;
	max?: number;
	color: ProgressBarColor;
	showNumericalProgress?: boolean;
	overRidingPixelHeight?: number;
}

const getColorPair = (
	color: ProgressBarColor,
): {
	barColor: string;
	backgroundColor: string;
} => {
	switch (color) {
		case "primary":
			return {
				barColor: theme.color.primary[500],
				backgroundColor: theme.color.primary[200],
			};
		case "warn":
			return {
				barColor: theme.color.warning[500],
				backgroundColor: theme.color.warning[200],
			};
		case "lilac":
			return {
				barColor: theme.color.lilac[500],
				backgroundColor: theme.color.lilac[200],
			};
		case "dark":
			return {
				barColor: theme.color.ui[1],
				backgroundColor: theme.color.ui[6],
			};
	}
};

const getPercent = (percent: number, max: number) => {
	if (percent > max) {
		return max;
	}
	if (percent < 0) {
		return 0;
	}

	return percent;
};

export const ProgressBar: FC<ProgressBarProps> = ({
	color,
	percent = 0,
	max = 100,
	showNumericalProgress = true,
	overRidingPixelHeight = 0,
}) => {
	const usePercent = getPercent(percent, max);
	const width = `${Math.floor(usePercent)}%`;
	const { barColor, backgroundColor } = getColorPair(color);

	return (
		<div className={styles.progressBar}>
			{showNumericalProgress && <span data-testid="progressBarIndicator_text">{width}</span>}
			<div>
				{overRidingPixelHeight ? (
					<span style={{ height: overRidingPixelHeight, backgroundColor }}>
						<span
							data-testid="progressBarIndicator_width"
							style={{ width: width, height: overRidingPixelHeight, backgroundColor: barColor }}
						></span>
					</span>
				) : (
					<span
						style={overRidingPixelHeight ? { height: overRidingPixelHeight, backgroundColor } : { backgroundColor }}
					>
						<span data-testid="progressBarIndicator_width" style={{ width: width, backgroundColor: barColor }}></span>
					</span>
				)}
			</div>
		</div>
	);
};
